/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  WebsocketListingProperties,
  WebsocketListingRequest,
  WebsocketListingResponse,
  WebsocketInitialProperties,
} from '../../interface/blocked-neverblocked.interface';
import { CommonFilterProperties } from '../../interface/filter.interface';
import { ConfigureListFilterResponse } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchNeverBlockedWebsocket = createAsyncThunk<
  WebsocketListingResponse,
  WebsocketListingRequest
>(
  'device-management-NeverBlockedWebsocket/fetchDevicesNeverBlockedWebsocket',
  async (data: WebsocketListingRequest, { dispatch }) => {
    try {
      const response = await device.fetchNeverBlockedWebsocket(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchNeverBlockedWebsocketFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'NeverBlockedWebsocket/fetchListPerformanceStationFilters',
  async (_, { dispatch }) => {
    try {
      const response = await device.fetchNeverBlockedWebsocket();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: WebsocketInitialProperties = {
  devices: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  selectedChargingPoint: null,
  deviceListLoading: false,
  filters: [],
  searchBy: '',
};

// Create the slice
export const NeverBlockedWebsocketSlice = createSlice({
  name: 'device-management-NeverBlockedWebsocket',
  initialState,
  reducers: {
      /**
     * Function to update selectedChargingPoint
     * @param state
     * @param action
     */
    setSelectedDevice: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * setVlbcSearchBy function to update the searchBy property in vlbcs state
     * @param state
     * @param action
     */
    setVlbcSearchBy: (state, action) => {
      state.searchBy = action.payload;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchNeverBlockedWebsocket.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNeverBlockedWebsocket.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload?.results?.length
          ? action.payload.results?.map((eachDevice: WebsocketListingProperties) => ({
              ...eachDevice,
              id: eachDevice?._id,
              connector1: eachDevice?.connectorsStatus?.connector1 || '',
              connector2: eachDevice?.connectorsStatus?.connector2 || '',
              blockUntil: ConvertUTCtoDate(eachDevice.blockUntil || ''),
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchNeverBlockedWebsocket.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
  },
});

// Export the actions
export const { setVlbcSearchBy,setSelectedDevice } = NeverBlockedWebsocketSlice.actions;
// Export the reducer
export default NeverBlockedWebsocketSlice.reducer;
